import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { formatarCnpj, useEmpresaFinanceira } from '@/contexts/EmpresaFinanceiraContext';
import { toast } from 'sonner';
import { format, subMonths } from 'date-fns';

function mesAnterior() {
  return format(subMonths(new Date(), 1), 'yyyy-MM');
}

export function ProntidaoContabilCard() {
  const { empresaId, empresa } = useEmpresaFinanceira();
  const [anoMes, setAnoMes] = useState(mesAnterior());
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const carregar = useCallback(async () => {
    if (!empresaId) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchWithAuth(`/api/contabilidade/readiness/${anoMes}?empresaId=${empresaId}`);
      if (!res.ok) throw new Error('Falha ao carregar prontidão');
      setData(await res.json());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro');
    } finally {
      setLoading(false);
    }
  }, [anoMes, empresaId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const enviarPacote = async () => {
    if (!empresaId) {
      toast.error('Selecione a empresa antes de enviar o pacote contábil.');
      return;
    }
    setSending(true);
    try {
      const res = await fetchWithAuth(`/api/contabilidade/enviar-pacote/${anoMes}?empresaId=${empresaId}`, {
        method: 'POST',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Falha no envio');
      toast.success(body.message || 'Pacote enviado');
      await carregar();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao enviar');
    } finally {
      setSending(false);
    }
  };

  const c = data?.contagens;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base">Prontidão contábil</CardTitle>
            <CardDescription>
              {empresa
                ? `${empresa.nome_fantasia} — CNPJ ${formatarCnpj(empresa.cnpj)}. Mês de referência: ${anoMes}.`
                : 'Selecione RST Serviços ou Sun7 Energy. A prontidão consolidada misturaria as duas contabilidades.'}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <input
              type="month"
              className="h-9 rounded-md border bg-background px-2 text-sm"
              value={anoMes}
              onChange={(e) => setAnoMes(e.target.value)}
            />
            <Button variant="outline" size="sm" onClick={carregar} disabled={loading}>
              Atualizar
            </Button>
            <Button size="sm" onClick={enviarPacote} disabled={sending || loading || !empresaId}>
              Enviar agora
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {loading && !data ? (
          <p className="text-muted-foreground">Carregando…</p>
        ) : data ? (
          <>
            <div className="flex flex-wrap gap-2">
              <Badge variant={data.pronto ? 'default' : 'destructive'}>
                {data.pronto ? 'Pronto' : 'Incompleto'}
              </Badge>
              {data.fechamento?.status && (
                <Badge variant="outline">Status: {data.fechamento.status}</Badge>
              )}
            </div>
            <p>
              Contas: {c?.total ?? 0} · Completas: {c?.completas ?? 0} · Faltantes:{' '}
              {c?.faltantes ?? 0} · Exceções: {c?.excecoes ?? 0}
            </p>
            {data.faltantes?.length > 0 && (
              <ul className="max-h-40 overflow-auto text-xs text-muted-foreground list-disc pl-4">
                {data.faltantes.slice(0, 15).map((f: any) => (
                  <li key={`${f.tipo}-${f.id}`}>
                    [{f.tipo}] {f.descricao} — falta {f.faltantes?.join(', ')}
                  </li>
                ))}
                {data.faltantes.length > 15 && (
                  <li>… e mais {data.faltantes.length - 15}</li>
                )}
              </ul>
            )}
          </>
        ) : (
          <p className="text-muted-foreground">Sem dados</p>
        )}
      </CardContent>
    </Card>
  );
}
